import React from "react";
import { Box, Theme, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Investment } from "@rentablo/api/apiv1";
import { useCurrencyDisplay } from "@providers/CurrencyDisplayProvider";
import { getValueByInvestmentAndBookingType } from "@helpers";
import {
  GainLossPercents,
  GainLossTypography,
  LoadingPlaceHolder,
  Modal,
} from "@rentablo/components";
import { Tabs, Tab } from "@rentablo/components";
import { NewsSection, StickyModalHeader } from "@components";
import { CompactRangeButton } from "@components/Chart/ChartControls";
import { RangeSelectorControlProps, useRangeSelectControl } from "@hooks";
import { getInvestmentUrl } from "@helpers/mainAppUrls";
import { MandatorConditional } from "@helpers/mandatorConditional/MandatorConditional";
import {
  getDisplayedChangeAbs,
  getDisplayedQuoteValue,
} from "../getDisplayedQuoteValue";
import { CHART_TAB_DATE_RANGES, ChartTab } from "./ChartTab";
import { ProtocolTab } from "./ProtocolTab";

// Tabs also passes activeTab; the range applies to both tabs.
const ModalRangeButton = ({
  rangeProps,
}: {
  rangeProps: RangeSelectorControlProps;
  activeTab?: string;
}) => (
  <Box pr={4}>
    <CompactRangeButton
      rangeProps={rangeProps}
      intervalTypes={CHART_TAB_DATE_RANGES}
      disablePortal
    />
  </Box>
);

interface Props {
  investment: Investment | undefined;
  isOpen: boolean;
  onClose: () => void;
  image?: string | null;
}

export const InvestmentDetailModal = ({
  investment,
  isOpen,
  onClose,
  image,
}: Props) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const { selectedCurrencyView, getDisplayCurrency } = useCurrencyDisplay();
  const rangeProps = useRangeSelectControl({ firstDate: new Date() });

  if (!investment)
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        width={isMobile ? "auto" : 500}
        height="auto"
        maxHeight={isMobile ? "650px" : "100%"}
        position={isMobile ? "bottom" : "centered"}
        contentPadding={isMobile ? "16px 16px" : undefined}
        zIndex={10000}
      >
        <LoadingPlaceHolder />
      </Modal>
    );

  const isOriginal = selectedCurrencyView === "original";
  const displayCurrency = getDisplayCurrency(investment.currency);

  const displayedQuote = getDisplayedQuoteValue(investment.quote, isOriginal);
  const displayedChangeAbs = getDisplayedChangeAbs(
    investment.quote,
    isOriginal
  );

  const quoteValue = getValueByInvestmentAndBookingType({
    value: displayedQuote,
    investmentType: investment.type,
    currency: displayCurrency,
    percentageRange: "0-100",
    quoteNotation: investment.quoteNotation,
  });

  const modalPadding = isMobile ? 4 : 5;

  const header = (
    <>
      <StickyModalHeader
        name={investment.standardisedName ?? ""}
        image={image}
        href={getInvestmentUrl(investment.id)}
        onClose={onClose}
      />
      <Box display="flex" mb={4}>
        <Box width="50%">
          <Typography variant="body4" color="textSecondary">
            {t("domains.investments.investmentTable.detailModal.currentQuote")}
          </Typography>
          <Typography variant="subtitle2">{quoteValue}</Typography>
        </Box>
        <Box width="50%">
          <Typography variant="body4" color="textSecondary">
            {t(
              "domains.investments.investmentTable.detailModal.performanceToday"
            )}
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <GainLossTypography variant="subtitle2" value={displayedChangeAbs}>
              {investment.quote?.changeAbs != null
                ? getValueByInvestmentAndBookingType({
                    value: displayedChangeAbs,
                    investmentType: investment.type,
                    currency: displayCurrency,
                    percentageRange: "0-100",
                    quoteNotation: investment.quoteNotation,
                  })
                : "–"}
            </GainLossTypography>
            <GainLossPercents value={investment.quote?.changePercentAbs ?? 0} />
          </Box>
        </Box>
      </Box>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={header}
      width={isMobile ? "auto" : 500}
      height="auto"
      maxHeight={isMobile ? "650px" : "100%"}
      position={isMobile ? "bottom" : "centered"}
      contentPadding={isMobile ? "16px 16px" : undefined}
      zIndex={10000}
    >
      <Box
        ml={-modalPadding}
        mr={-modalPadding}
        width={`calc(100% + ${modalPadding * 2} * 4px)`}
        display="flex"
        flexDirection="column"
        flex={1}
      >
        <Tabs
          keepRendered
          sx={{
            pl: 2,
          }}
          buttonElement={
            isMobile ? <ModalRangeButton rangeProps={rangeProps} /> : undefined
          }
        >
          <Tab
            key="fundamentals"
            label={t(
              "domains.investments.investmentTable.detailModal.fundamentals"
            )}
          >
            <ChartTab
              investment={investment}
              rangeProps={rangeProps}
              hideRangeSelector={isMobile}
            />
          </Tab>
          <Tab
            key="protocol"
            label={t(
              "domains.investments.investmentTable.detailModal.protocol"
            )}
          >
            <ProtocolTab
              investment={investment}
              displayCurrency={displayCurrency}
              rangeProps={isMobile ? rangeProps : undefined}
            />
          </Tab>
        </Tabs>

        <MandatorConditional hideFor={["kpmg-germany"]}>
          <Box>
            <NewsSection isin={investment.isin} />
          </Box>
        </MandatorConditional>
      </Box>
    </Modal>
  );
};
